import { SubscriptionData } from '@/models/User';

export interface SubscriptionStatus {
  isActive: boolean;
  isTrialing: boolean;
  isExpired: boolean;
  isTrialExpired: boolean;
  hasValidPayment: boolean;
  daysUntilExpiry?: number;
  daysUntilTrialExpiry?: number;
}

/**
 * Check if user has an active subscription
 */
export function hasActiveSubscription(subscription?: SubscriptionData): boolean {
  if (!subscription) return false;
  
  const now = Math.floor(Date.now() / 1000);
  
  // Check if subscription is active and not expired
  if (subscription.status === 'active') {
    if (subscription.current_period_end) {
      return now < subscription.current_period_end;
    }
    return true;
  }
  
  return false;
}

/**
 * Check if user is in trial period
 */
export function isInTrialPeriod(subscription?: SubscriptionData): boolean {
  if (!subscription) return false;
  
  const now = Math.floor(Date.now() / 1000);
  
  // Check if subscription is trialing and trial hasn't expired
  if (subscription.status === 'trialing' && subscription.trial_end) {
    return now < subscription.trial_end;
  }
  
  return false;
}

/**
 * Check if trial has expired
 */
export function isTrialExpired(subscription?: SubscriptionData): boolean {
  if (!subscription) return false;
  
  const now = Math.floor(Date.now() / 1000);
  
  // If status is trialing but trial_end has passed
  if (subscription.status === 'trialing' && subscription.trial_end) {
    return now >= subscription.trial_end;
  }
  
  return false;
}

/**
 * Check if subscription has expired
 */
export function isSubscriptionExpired(subscription?: SubscriptionData): boolean {
  if (!subscription) return false;
  
  const now = Math.floor(Date.now() / 1000);
  
  // Check if current period has ended
  if (subscription.current_period_end) {
    return now >= subscription.current_period_end;
  }
  
  return false;
}

/**
 * Get comprehensive subscription status
 */
export function getSubscriptionStatus(subscription?: SubscriptionData): SubscriptionStatus {
  const isActive = hasActiveSubscription(subscription);
  const isTrialing = isInTrialPeriod(subscription);
  const isExpired = isSubscriptionExpired(subscription);
  const isTrialExpiredFlag = isTrialExpired(subscription);
  const hasValidPayment = isActive || isTrialing;
  
  let daysUntilExpiry: number | undefined;
  let daysUntilTrialExpiry: number | undefined;
  
  if (subscription) {
    const now = Math.floor(Date.now() / 1000);
    
    if (subscription.current_period_end) {
      const days = Math.ceil((subscription.current_period_end - now) / (24 * 60 * 60));
      daysUntilExpiry = Math.max(0, days);
    }
    
    if (subscription.trial_end) {
      const days = Math.ceil((subscription.trial_end - now) / (24 * 60 * 60));
      daysUntilTrialExpiry = Math.max(0, days);
    }
  }
  
  return {
    isActive,
    isTrialing,
    isExpired,
    isTrialExpired: isTrialExpiredFlag,
    hasValidPayment,
    daysUntilExpiry,
    daysUntilTrialExpiry,
  };
}

/**
 * Check if user has access to paid features
 */
export function hasPaidAccess(subscription?: SubscriptionData, userRole?: 'admin' | 'user'): boolean {
  // Admin users always have access regardless of subscription status
  if (userRole === 'admin') {
    return true;
  }
  
  const status = getSubscriptionStatus(subscription);
  return status.hasValidPayment;
}

/**
 * Get subscription display status for UI
 */
export function getSubscriptionDisplayStatus(subscription?: SubscriptionData): string {
  if (!subscription) return 'No Subscription';
  
  const status = getSubscriptionStatus(subscription);
  
  if (status.isActive) {
    return 'Active';
  } else if (status.isTrialing) {
    return `Trial (${status.daysUntilTrialExpiry} days left)`;
  } else if (status.isTrialExpired) {
    return 'Trial Expired';
  } else if (status.isExpired) {
    return 'Expired';
  } else {
    return subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1);
  }
}
